import React from 'react';
import { ImageDto } from '@src/types/types';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /**
   * The image data with formats information
   */
  image: ImageDto;

  /**
   * CSS sizes attribute for responsive images
   * e.g. "(max-width: 768px) 100vw, 50vw"
   * @default "100vw"
   */
  sizes?: string;

  /**
   * Whether to lazy load the image
   * @default true
   */
  lazy?: boolean;

  /**
   * Class name for the image
   */
  className?: string;

  /**
   * Alt text for the image
   */
  alt: string;
}

/**
 * Responsive image component that generates srcset from ImageDto
 */
export const Image: React.FC<ImageProps> = ({
  image,
  sizes = '100vw',
  lazy = true,
  className,
  alt,
  ...props
}) => {
  if (!image) {
    return null;
  }

  // Sort formats by width for srcset
  const sortedFormats = [...image.formats].sort((a, b) => a.width - b.width);

  // Generate srcset string from formats
  const srcset = sortedFormats
    .map((format) => `${format.url} ${format.width}w`)
    .join(', ');

  return (
    <img
      src={image.url}
      srcSet={srcset}
      sizes={sizes}
      alt={alt}
      loading={lazy ? 'lazy' : undefined}
      width={image.width}
      height={image.height}
      className={className}
      {...props}
    />
  );
};
